import { defineNuxtRouteMiddleware, useRuntimeConfig } from '#imports'

import { merge } from '@backmarket/utils/object/merge'

import { useRedirectLocale } from '../composables/useRedirectLocale'
import { useUserStore } from '../composables/useUserStore'
import type { AuthLevel } from '../types'
import { findRouteOrParentMatchingPredicate } from '../utils/findRouteOrParentMatchingPredicate'

function getAuthLevel(isAuthenticated: boolean, isGuest: boolean): AuthLevel {
  if (isAuthenticated) return 'authenticated'
  if (isGuest) return 'guest'
  return 'unauthenticated'
}

export default defineNuxtRouteMiddleware(async (route) => {
  const config = useRuntimeConfig().public.oauth
  const { isUserFetched, isUserAuthenticated, isUserGuest, fetchUser } =
    useUserStore()

  const matchedRoute = findRouteOrParentMatchingPredicate(
    route,
    (routeDefinition) =>
      routeDefinition.meta !== undefined &&
      routeDefinition.meta.auth !== undefined,
  )

  // No auth meta in the route hierarchy = public route
  if (matchedRoute === undefined) return undefined

  const routeAuth = matchedRoute.meta.auth
  if (routeAuth === undefined) return undefined

  // Guest magic-link flow — token exchange, session establishment, etc.
  if (routeAuth.mode === 'guest-magic-link') {
    // TODO: [IDENTITY-5011] implement guest magic link flow
    return undefined
  }

  // No restriction, anyone passes (overrides a restricted parent)
  if (routeAuth.mode === 'public') return undefined

  if (!isUserFetched.value) {
    await fetchUser()
  }

  const currentLevel = getAuthLevel(
    isUserAuthenticated.value,
    isUserGuest.value,
  )

  if (routeAuth.mode.includes(currentLevel)) return undefined

  const { getRouteLocationWithLocale } = useRedirectLocale()

  // Route-level exception (per-level, then default), then the app-level default
  const redirect =
    routeAuth.redirection?.[currentLevel] ??
    routeAuth.redirection?.default ??
    getRouteLocationWithLocale(config.defaultRedirectLocations[currentLevel])

  return merge({}, redirect, { query: { next: route.fullPath } })
})
